<template>
    <component :is="Loading" v-if="loading" />
    <component
        :is="Component"
        v-else
        :can-sign="canSign"
        v-bind="forwardedProps"
    />
</template>

<script lang="ts">
    import { Component, Prop, Watch, Vue } from 'vue-property-decorator';

    import type { IamClient } from '@evidentid/iam-client';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import { useTermsStore } from '../store/terms';

    @Component
    export default class AuthTermsWrapper extends Vue {
        @Prop()
        private Component!: Vue;

        @Prop()
        private Loading!: Vue;

        @Prop({ type: String })
        private content!: string;

        @Prop({ type: String })
        private hash!: string;

        private canSignRp: string | null = null;
        private canSign: boolean | null = null;

        // PRODUCT-26110 Task 10 (INSTANCE_LISTENERS): Vue's template compiler rejects
        // two bare `v-bind` directives on the same element ("Duplicate attribute"), so
        // `v-bind="$props"` and the former `v-on="$listeners"` (now `$attrs`, which
        // carries listeners as `onXxx` keys) must be merged into a single object.
        private get forwardedProps(): Record<string, unknown> {
            return { ...this.$props, ...this.$attrs };
        }

        private get iam(): IamClient {
            return (this as any).$auth.client;
        }

        private get rpId(): string {
            // vue-router 4 types repeated params as `string | string[]`, stricter
            // than vue-router 3 -- this route has no repeated `rpId` segment, so
            // `as string` matches ed2202df8's established narrowing.
            return this.$route.params.rpId as string;
        }

        private get currentSign(): any {
            return useTermsStore().signDetails[this.rpId];
        }

        private get loading(): boolean {
            return (
                !this.currentSign ||
                this.currentSign.status === OperationStatus.loading ||
                this.canSign === null
            );
        }

        @Watch('$route.params.rpId')
        private onRpChange(): void {
            this.recalculate();
        }

        private mounted(): void {
            this.recalculate();
        }

        private async recalculate(): Promise<void> {
            const rpName = this.rpId;
            const currentSign = this.currentSign;

            // Change permissions status to indeterminate
            if (this.canSignRp !== rpName) {
                this.canSign = null;
                this.canSignRp = rpName;
            }

            // Check the permissions
            const [ canLoadSign, canSign ] = await Promise.all([
                this.iam.obtainPermissions({
                    permission: 'read:request',
                    service: 'rpweb',
                }),
                this.iam.obtainPermissions({
                    permission: 'manage:configuration',
                    service: 'rpweb',
                }),
            ]);

            // Stop when there is race condition
            if (this.canSignRp !== rpName) {
                return;
            }

            // Save information about permissions
            this.canSign = canSign;

            // Don't reload data without need
            if (currentSign && currentSign.status !== OperationStatus.uninitialized) {
                return;
            }

            // Load sign details or show information that the User is not authorized
            if (canLoadSign) {
                await useTermsStore().loadSignDetails({ rpName });
            } else {
                this.$router.push({
                    name: 'error.rp',
                    params: { reason: 'unauthorized' },
                    query: { next: this.$route.query.next || this.$route.query.fullPath },
                });
            }
        }
    }
</script>
